import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { findUnknownPlaceholders } from '@/lib/promptPlaceholders';
import type { CatalogDebug, CatalogPlan } from '@/types';

interface CatalogAnalyzePanelProps {
  // Trockenlauf: Berater + Assemblierung, kein Bild. Gibt CatalogDebug (mit plan) zurück.
  onAnalyze: (consultOverride: string, renderTemplateOverride: string) => Promise<CatalogDebug | null>;
  // „Mit diesem Prompt generieren": echtes Staged-Bild aus dem (evtl. editierten) Plan.
  onGenerateWithPlan: (plan: CatalogPlan) => void;
}

// Admin-only: editiert Berater-/Render-Prompt, testet das End-Prompt trocken,
// generiert deterministisch aus dem analysierten Plan, speichert Templates dauerhaft.
export const CatalogAnalyzePanel: React.FC<CatalogAnalyzePanelProps> = ({ onAnalyze, onGenerateWithPlan }) => {
  const [consultPrompt, setConsultPrompt] = useState('');
  const [renderTemplate, setRenderTemplate] = useState('');
  const [debug, setDebug] = useState<CatalogDebug | null>(null);
  const [editedRenderPrompt, setEditedRenderPrompt] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<{ key: string; ok: boolean; text: string } | null>(null);

  // Beide Prompts beim Öffnen direkt aus der DB vorbefüllen, damit der echte
  // gespeicherte Prompt sichtbar/editierbar ist (nicht erst nach „Analysieren").
  useEffect(() => {
    let active = true;
    supabase
      .from('prompt_templates')
      .select('key, content')
      .in('key', ['stage_consult_system', 'stage_catalog_render'])
      .then(({ data }) => {
        if (!active || !data) return;
        for (const row of data as { key: string; content: string }[]) {
          if (row.key === 'stage_consult_system' && row.content) setConsultPrompt((v) => v || row.content);
          if (row.key === 'stage_catalog_render' && row.content) setRenderTemplate((v) => v || row.content);
        }
      });
    return () => { active = false; };
  }, []);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      const result = await onAnalyze(consultPrompt, renderTemplate);
      setDebug(result);
      // Textareas mit effektivem Inhalt vorbefüllen (erstes Analysieren zeigt DB-Standard).
      if (result?.effectiveConsultPrompt && !consultPrompt.trim()) setConsultPrompt(result.effectiveConsultPrompt);
      if (result?.effectiveRenderTemplate && !renderTemplate.trim()) setRenderTemplate(result.effectiveRenderTemplate);
      setEditedRenderPrompt(result?.plan?.renderPrompt ?? result?.renderPrompt ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async (key: string, content: string, category: 'system' | 'room_staged') => {
    const unknown = findUnknownPlaceholders(content, category);
    if (unknown.length) {
      setSaveMsg({ key, ok: false, text: `Unbekannte Platzhalter: ${unknown.join(', ')}` });
      return;
    }
    setSavingKey(key);
    setSaveMsg(null);
    try {
      const { error: saveErr } = await supabase.from('prompt_templates').update({ content }).eq('key', key);
      if (saveErr) throw saveErr;
      setSaveMsg({ key, ok: true, text: 'Gespeichert' });
    } catch (e) {
      setSaveMsg({ key, ok: false, text: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' });
    } finally {
      setSavingKey(null);
    }
  };

  const canGenerate = !!debug?.plan && editedRenderPrompt.trim().length > 0;

  return (
    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 text-sm p-4 space-y-3">
      <div className="flex items-center gap-2 font-medium text-foreground">
        <Sparkles className="w-4 h-4 text-primary" />
        Katalog-Analyse (Admin)
      </div>

      <div className="space-y-1">
        <label className="block text-[11px] font-medium text-slate-600">
          Berater-System-Prompt (vorbefüllt aus DB — bearbeiten &amp; speichern)
        </label>
        <textarea
          value={consultPrompt}
          onChange={(e) => setConsultPrompt(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
          placeholder="leer lassen für den gespeicherten Berater-Prompt"
        />
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={savingKey === 'stage_consult_system'}
            onClick={() => handleSave('stage_consult_system', consultPrompt, 'system')}>
            💾 Berater-Prompt speichern
          </Button>
          {saveMsg?.key === 'stage_consult_system' && (
            <span className={saveMsg.ok ? 'text-emerald-600 text-[11px]' : 'text-red-600 text-[11px]'}>{saveMsg.text}</span>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <label className="block text-[11px] font-medium text-slate-600">
          Render-Template (Platzhalter: {'{{ROOM_TYPE}} {{STYLE}} {{PLACEMENT_LIST}} {{ASPECT_RATIO_INSTRUCTION}}'}; vorbefüllt aus DB — bearbeiten &amp; speichern)
        </label>
        <textarea
          value={renderTemplate}
          onChange={(e) => setRenderTemplate(e.target.value)}
          rows={5}
          className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
          placeholder="leer lassen für das gespeicherte Render-Template"
        />
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={savingKey === 'stage_catalog_render'}
            onClick={() => handleSave('stage_catalog_render', renderTemplate, 'room_staged')}>
            💾 Render-Template speichern
          </Button>
          {saveMsg?.key === 'stage_catalog_render' && (
            <span className={saveMsg.ok ? 'text-emerald-600 text-[11px]' : 'text-red-600 text-[11px]'}>{saveMsg.text}</span>
          )}
        </div>
      </div>

      <Button size="sm" onClick={handleAnalyze} disabled={analyzing}>
        {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Analysieren (kein Bild, keine Credits)
      </Button>

      {error && <p className="text-red-600 text-[12px]">⚠ {error} — kein Credit verbraucht.</p>}

      {debug && (
        <div className="space-y-2 border-t border-primary/20 pt-3">
          <p className="text-xs text-muted-foreground">{debug.candidateCount} Moodboard-Kandidat(en) verfügbar.</p>
          {debug.fallbackReason && <p className="text-sm text-amber-600">⚠ {debug.fallbackReason}</p>}
          {debug.selected.length > 0 && (
            <ul className="space-y-1">
              {debug.selected.map((s, i) => (
                <li key={i} className="text-sm">
                  <span className="font-medium">Bild {i + 2}</span> · {s.category}
                  {s.name ? ` — ${s.name}` : ''}{s.positionHint ? ' → ' : ''}
                  <span className="text-muted-foreground">{s.positionHint}</span>
                </li>
              ))}
            </ul>
          )}
          {debug.notes && <p className="text-xs text-muted-foreground"><span className="font-semibold">Notiz:</span> {debug.notes}</p>}
          {debug.plan && (
            <div className="space-y-1">
              <label className="block text-[11px] font-medium text-slate-600">
                Finaler Render-Prompt (editierbar — geht exakt so ans Bild-Modell):
              </label>
              <textarea
                value={editedRenderPrompt}
                onChange={(e) => setEditedRenderPrompt(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
              />
            </div>
          )}
          <Button size="sm" disabled={!canGenerate}
            onClick={() => debug.plan && onGenerateWithPlan({ renderPrompt: editedRenderPrompt, pieces: debug.plan.pieces })}>
            Mit diesem Prompt generieren
          </Button>
        </div>
      )}
    </div>
  );
};

export default CatalogAnalyzePanel;
